import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import DocumentUpload from '../../components/DocumentUpload.jsx';
import LocationFields from '../../components/LocationFields.jsx';
import LanguageSelect from '../../components/LanguageSelect.jsx';
import AvatarUpload from '../../components/AvatarUpload.jsx';
import PhoneField from '../../components/PhoneField.jsx';
import SearchableMultiSelect from '../../components/SearchableMultiSelect.jsx';
import ServiceIcon from '../../components/ServiceIcon.jsx';
import Icon from '../../components/Icon.jsx';
import { useAuth } from '../../lib/AuthContext.jsx';
import {
  Avatar,
  Stars,
  Loading,
  useToast,
  Field,
  StatusPill,
  Tabs,
  Switch,
  Empty,
  PageHeader,
} from '../../components/ui.jsx';
import { isValidPhone } from '../../lib/validation.js';
import { ALL_CITIES } from '../../lib/locations.js';
import { currency, relativeTime } from '../../lib/format.js';

const VERIFY_LABEL = { verified: 'Verified', pending: 'Verification pending', rejected: 'Rejected' };

export default function NurseProfileEdit() {
  const { user, nurseProfile, refresh } = useAuth();
  const toast = useToast();

  const [tab, setTab] = useState('personal');
  const [services, setServices] = useState([]);
  const [serviceIds, setServiceIds] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [account, setAccount] = useState({ name: '', phone: '', gender: '', address: '', city: '' });
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kinds, setKinds] = useState([]);
  const [docs, setDocs] = useState({ documents: [], missingRequired: [] });
  const [uploading, setUploading] = useState('');

  const loadDocuments = useCallback(() => api.get('/api/documents/mine').then(setDocs).catch(() => {}), []);

  useEffect(() => {
    api.get('/api/services').then(setServices).catch(() => {});
    api.get('/api/document-kinds').then((d) => setKinds(d.kinds)).catch(() => setKinds([]));
    loadDocuments();
  }, [loadDocuments]);

  /** Uploading a replacement after a rejection re-opens the review. */
  const uploadDocument = async (kind, file) => {
    setUploading(kind);
    try {
      await api.upload('/api/documents', file, { kind, filename: file.name });
      await loadDocuments();
      await refresh();
      toast.success('Document uploaded — an administrator will review it.');
    } catch (err) {
      toast.error(err);
    } finally {
      setUploading('');
    }
  };

  const removeDocument = async (kind) => {
    const doc = docs.documents.find((d) => d.kind === kind);
    if (!doc) return;
    try {
      await api.del(`/api/documents/${doc.id}`);
      await loadDocuments();
      toast.success('Document removed.');
    } catch (err) {
      toast.error(err);
    }
  };

  useEffect(() => {
    if (!user || !nurseProfile) return;
    setAccount({
      name: user.name || '',
      phone: user.phone || '',
      gender: user.gender || '',
      address: user.address || '',
      city: user.city || '',
      state: user.state || '',
      lat: user.lat ?? null,
      lng: user.lng ?? null,
    });
    setProfile({
      headline: nurseProfile.headline || '',
      bio: nurseProfile.bio || '',
      experienceYears: nurseProfile.experience_years,
      hourlyRate: nurseProfile.hourly_rate,
      languages: nurseProfile.languages || '',
      qualifications: nurseProfile.qualifications || '',
      certifications: nurseProfile.certifications || '',
      serviceAreas: nurseProfile.service_areas || '',
      licenseNumber: nurseProfile.license_number || '',
      isAvailable: !!nurseProfile.is_available,
    });
    api
      .get(`/api/nurses/${user.id}`)
      .then((n) => {
        setServiceIds(n.services.map((s) => s.id));
        setReviews(n.reviews);
      })
      .catch(() => {});
  }, [user, nurseProfile]);

  if (!profile) return <Loading rows={3} />;

  const setP = (k) => (e) => setProfile((p) => ({ ...p, [k]: e.target.value }));
  const setA = (k) => (e) => setAccount((a) => ({ ...a, [k]: e.target.value }));
  const toggleService = (id) =>
    setServiceIds((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const save = async (e) => {
    e.preventDefault();
    if (serviceIds.length === 0) return toast.error('Select at least one service you provide.');
    if (account.phone && !isValidPhone(account.phone)) return toast.error('Enter a valid phone number.');
    setBusy(true);
    try {
      await api.put('/api/auth/me', account);
      await api.put('/api/nurses/me/profile', { ...profile, serviceIds });
      await refresh();
      toast.success('Profile saved.');
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };

  const toggleAvailable = async () => {
    const next = !profile.isAvailable;
    setProfile((p) => ({ ...p, isAvailable: next }));
    try {
      await api.put('/api/nurses/me/profile', { isAvailable: next });
      await refresh();
      toast.success(next ? 'You are now visible to patients.' : 'You are now hidden from search.');
    } catch (err) {
      toast.error(err);
      setProfile((p) => ({ ...p, isAvailable: !next }));
    }
  };

  return (
    <>
      <PageHeader
        title={user.name}
        avatar={<Avatar name={user.name} avatar={user.avatar} size="lg" ring />}
        subtitle={`${nurseProfile.jobs_completed} visits · ${currency(nurseProfile.hourly_rate)}/hr`}
        badge={
          <StatusPill status={nurseProfile.verification_status} label={VERIFY_LABEL[nurseProfile.verification_status]} />
        }
      >
        <Link to={`/nurses/${user.id}`} className="btn btn-ghost">
          <Icon name="eye" /> Preview public profile
        </Link>
      </PageHeader>

      <div className="row-tight mb-3">
        <Stars value={nurseProfile.rating_avg} count={nurseProfile.rating_count} />
      </div>

      {nurseProfile.verification_status === 'rejected' && (
        <div className="banner banner-danger">
          <Icon name="warning" />
          <span className="grow">
            <strong>Verification unsuccessful.</strong>{' '}
            {nurseProfile.rejection_reason || 'Update your licence details below to re-submit for review.'}
          </span>
        </div>
      )}

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'personal', label: 'Personal details' },
          { key: 'professional', label: 'Professional' },
          { key: 'documents', label: 'Documents', badge: docs.missingRequired.length || undefined },
        ]}
      />

      {/* Personal Details — shown first for every user type (consistent layout). */}
      {tab === 'personal' && (
        <form onSubmit={save} className="detail-layout">
          <section className="card">
            <div className="card-header">
              <h2>Personal details</h2>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <AvatarUpload />
              </div>
              <div className="grid grid-2">
                <Field label="Full name" id="n-name">
                  <input id="n-name" type="text" value={account.name} onChange={setA('name')} required />
                </Field>
                <PhoneField id="n-phone" value={account.phone} onChange={(v) => setAccount((a) => ({ ...a, phone: v }))} />
              </div>
              <Field label="Gender" id="n-gender" hint="Patients can filter by this.">
                <select id="n-gender" value={account.gender} onChange={setA('gender')}>
                  <option value="">Prefer not to say</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="other">Other</option>
                </select>
              </Field>

              <hr className="divider" />
              <span className="t-label mb-1">Where you are based</span>

              <LocationFields
                value={{
                  state: account.state,
                  city: account.city,
                  address: account.address,
                  lat: account.lat,
                  lng: account.lng,
                }}
                onChange={(patch) => setAccount((a) => ({ ...a, ...patch }))}
                required={false}
              />
            </div>
            <div className="card-footer">
              <button className="btn btn-primary" disabled={busy}>
                {busy ? <span className="spinner" /> : 'Save changes'}
              </button>
            </div>
          </section>

          <aside className="stack sticky-aside">
            {/* Going on/off duty is the single most-used control here, so it gets
                its own card rather than hiding at the bottom of a long form. */}
            <section className="card">
              <div className="card-header">
                <span className={`icon-tile ${profile.isAvailable ? '' : 'icon-tile-neutral'}`} style={{ width: 34, height: 34 }}>
                  <Icon name={profile.isAvailable ? 'bell' : 'bell-off'} size={16} />
                </span>
                <h3>Booking status</h3>
              </div>
              <div className="card-body">
                <Switch
                  checked={profile.isAvailable}
                  onChange={toggleAvailable}
                  label="Accepting new bookings"
                  hint={profile.isAvailable ? 'You appear in patient search results.' : 'You are hidden from search.'}
                />
                <p className="tiny dim mt-2">
                  Turn this off while on leave — existing confirmed visits are unaffected.
                </p>
              </div>
            </section>
          </aside>
        </form>
      )}

      {/* Professional Details — nurse only. */}
      {tab === 'professional' && (
        <form onSubmit={save} className="detail-layout">
          <div className="stack">
            <section className="card">
              <div className="card-header">
                <h2>Public profile</h2>
              </div>
              <div className="card-body">
                <Field label="Headline" id="n-headline" hint="One line patients see first.">
                  <input
                    id="n-headline"
                    type="text"
                    value={profile.headline}
                    onChange={setP('headline')}
                    placeholder="Post-operative care nurse · 8 years"
                  />
                </Field>
                <Field label="About you" id="n-bio">
                  <textarea id="n-bio" value={profile.bio} onChange={setP('bio')} rows={5} />
                </Field>
                <div className="grid grid-2">
                  <Field label="Years of experience" id="n-exp" required>
                    <input
                      id="n-exp"
                      type="number"
                      min="0"
                      max="60"
                      value={profile.experienceYears}
                      onChange={setP('experienceYears')}
                      required
                    />
                  </Field>
                  <Field label="Hourly rate (₹)" id="n-rate" required>
                    <input
                      id="n-rate"
                      type="number"
                      min="0"
                      step="10"
                      value={profile.hourlyRate}
                      onChange={setP('hourlyRate')}
                      required
                    />
                  </Field>
                </div>

                <hr className="divider" />
                <span className="t-label mb-1">Reach &amp; services</span>

                <Field label="Languages spoken" id="n-lang" hint="Choose every language you speak.">
                  <LanguageSelect
                    id="n-lang"
                    value={profile.languages}
                    onChange={(v) => setProfile((p) => ({ ...p, languages: v }))}
                  />
                </Field>
                <Field
                  label="Service areas"
                  id="n-areas"
                  hint="Cities you travel to for visits — used to match nearby patients."
                >
                  <SearchableMultiSelect
                    id="n-areas"
                    options={ALL_CITIES}
                    value={profile.serviceAreas}
                    onChange={(v) => setProfile((p) => ({ ...p, serviceAreas: v }))}
                    placeholder="Search a city to add…"
                    emptyText="No matching city"
                  />
                </Field>
                <Field label="Services offered" hint="Patients can only book you for the services you select.">
                  <div className="chips">
                    {services.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="chip"
                        aria-pressed={serviceIds.includes(s.id)}
                        onClick={() => toggleService(s.id)}
                      >
                        <ServiceIcon service={s} size={16} /> {s.name}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
            </section>

            <section className="card">
              <div className="card-header">
                <span className="icon-tile icon-tile-neutral" style={{ width: 34, height: 34 }}>
                  <Icon name="award" size={16} />
                </span>
                <h2 className="grow">Qualifications</h2>
                <span className="tiny dim">Reviewed by an administrator</span>
              </div>
              <div className="card-body">
                <Field label="Qualifications" id="n-qual">
                  <input id="n-qual" type="text" value={profile.qualifications} onChange={setP('qualifications')} />
                </Field>
                <Field label="Certifications" id="n-cert" hint="Comma separated.">
                  <input id="n-cert" type="text" value={profile.certifications} onChange={setP('certifications')} />
                </Field>
                <Field label="Licence number" id="n-lic">
                  <input id="n-lic" type="text" value={profile.licenseNumber} onChange={setP('licenseNumber')} />
                </Field>
              </div>
              <div className="card-footer">
                <button className="btn btn-primary" disabled={busy}>
                  {busy ? <span className="spinner" /> : 'Save changes'}
                </button>
              </div>
            </section>
          </div>

          <aside className="stack sticky-aside">
            <section className="card">
              <div className="card-header">
                <h3 className="grow">Reviews</h3>
                <Stars value={nurseProfile.rating_avg} count={nurseProfile.rating_count} />
              </div>
              <div className="card-body">
                {reviews.length === 0 ? (
                  <Empty art="support" title="No reviews yet">
                    Patient feedback appears here after your first completed visit.
                  </Empty>
                ) : (
                  reviews.slice(0, 6).map((r) => (
                    <div key={r.id} className="review">
                      <div className="review-head">
                        <Avatar name={r.patient_name} avatar={r.patient_avatar} size="sm" />
                        <div className="grow">
                          <div className="bold small truncate">{r.patient_name}</div>
                          <div className="tiny dim">{relativeTime(r.created_at)}</div>
                        </div>
                        <Stars value={r.rating} />
                      </div>
                      {r.comment && <p className="tiny">{r.comment}</p>}
                    </div>
                  ))
                )}
              </div>
            </section>
          </aside>
        </form>
      )}

      {/* Documents — nurse only. */}
      {tab === 'documents' && (
        <section className="card">
          <div className="card-header">
            <h2 className="grow">Documents</h2>
            {docs.missingRequired.length > 0 && (
              <span className="pill pill-warning">
                <Icon name="warning" /> {docs.missingRequired.length} outstanding
              </span>
            )}
          </div>
          <div className="card-body">
            <div className="banner banner-neutral">
              <Icon name="lock" />
              <span className="grow">
                Only you and the reviewing administrator can open these files. Replacing a rejected document puts
                your profile back in the review queue.
              </span>
            </div>
            {kinds.map((k) => {
              const doc = docs.documents.find((d) => d.kind === k.key);
              return (
                <DocumentUpload
                  key={k.key}
                  kind={k.key}
                  label={k.label}
                  hint={k.hint}
                  required={k.required}
                  existing={doc}
                  status={
                    uploading === k.key
                      ? { tone: 'accent', text: 'uploading…' }
                      : doc
                        ? {
                            tone: { approved: 'success', rejected: 'danger', pending: 'warning' }[doc.status],
                            text: doc.status,
                          }
                        : null
                  }
                  error={doc?.status === 'rejected' ? doc.review_note || 'Rejected — please upload a clearer copy.' : ''}
                  onPick={uploadDocument}
                  onClear={doc && doc.status !== 'approved' ? removeDocument : undefined}
                />
              );
            })}
          </div>
        </section>
      )}
    </>
  );
}
